import { BigNum, BN, SpotMarketConfig, ZERO } from '@drift-labs/sdk';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import { CollateralInputV2 } from 'src/components/Inputs/CollateralInput';
import { InputQuickActions } from 'src/components/Inputs/InputQuickActions';
import { redeemPeriodToString } from 'src/utils/vault/withdraw';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';

interface VaultInputProps {
	isDeposit: boolean;
	withdrawalCtaState: {
		displayInput: boolean;
	};
	maxAmount: BigNum;
	depositAssetConfig: SpotMarketConfig;
	handleOnValueChange: (value?: string) => void;
	inputAmount: string;
	isLoading: boolean;
	setIsClickedMaxWithdrawal: (value: boolean) => void;
	redemptionPeriod?: BN;
	minDepositAmount?: BN;
}

const VaultInput = ({
	isDeposit,
	withdrawalCtaState,
	maxAmount,
	depositAssetConfig,
	handleOnValueChange,
	inputAmount,
	isLoading,
	setIsClickedMaxWithdrawal,
	redemptionPeriod,
	minDepositAmount,
}: VaultInputProps) => {
	const isInputAmountLessThanMinDepositAmount =
		minDepositAmount?.gtn(0) &&
		!isNaN(+inputAmount) &&
		+inputAmount > 0 &&
		BigNum.fromPrint(inputAmount, depositAssetConfig.precisionExp).val.lt(
			minDepositAmount
		);

	const minDepositErrorMessage =
		isDeposit && isInputAmountLessThanMinDepositAmount
			? `The minimum deposit amount is ${BigNum.from(
					minDepositAmount ?? ZERO,
					depositAssetConfig.precisionExp
				).prettyPrint()} ${depositAssetConfig.symbol}`
			: undefined;

	const shouldDisableInput = !isDeposit && !withdrawalCtaState.displayInput;
	const isWalletConnected = useWalletIsConnected();

	return (
		<div className="flex flex-col gap-2">
			<CollateralInputV2
				maxAmount={maxAmount}
				selectedMarket={depositAssetConfig}
				banks={[depositAssetConfig]}
				onChangeValue={handleOnValueChange}
				value={inputAmount}
				isFixedAsset
				label={
					<div className="flex items-center gap-1">
						Amount
						{!isDeposit && (
							<Tooltip content="The maximum amount is after fees, while the final amount received may differ from the amount requested." />
						)}
					</div>
				}
				source={{
					type: 'wallet',
				}}
				error={minDepositErrorMessage}
				customElementRight={
					!shouldDisableInput && isWalletConnected ? (
						<div className="flex items-center gap-1">
							<InputQuickActions.MaxButton
								onClick={() => {
									if (!isDeposit) {
										setIsClickedMaxWithdrawal(true);
									}
									handleOnValueChange(maxAmount.print());
								}}
								maxAmount={maxAmount}
								isLoading={isLoading}
							/>
						</div>
					) : null
				}
				disabled={shouldDisableInput}
			/>
			<span className="typo-b4 text-text-secondary">
				{isDeposit
					? `Deposited funds are subject to a${' '}
								${redeemPeriodToString(redemptionPeriod?.toNumber())} ${' '}
								redemption period.`
					: `After the ${' '}
								${redeemPeriodToString(redemptionPeriod?.toNumber())} ${' '}
								redemption period, your funds can be withdrawn to your
								wallet.`}
			</span>
		</div>
	);
};

export default VaultInput;
